import React, { useCallback } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import Input from '../../Forms/Input';
import Select from '../../Forms/Select';
import Checkbox from '../../Forms/Checkbox';
import { Cancel, Button, Avatar } from '../../../Styles/Styles';
import useForm from '../../../Hooks/useForm';
import { CALENDAR_REPEAT } from '../../../Library/Variables';
import {
  addHours,
  cap,
  getWeekNumber,
  ISODateString,
  isSameDate,
  isValidTime,
  parseTime,
  timeBetween
} from '../../../Library/Library';
import { Regexp, ZIndex } from '../../../Resources/Stylesheets/Variables';
import { addErr } from '../../../Reducers/Error/actions';
import DatePicker from '../../Forms/DatePicker';
import { useAddBasketProduct } from '../../../Library/Hooks';
import { useAfterSuccess } from '../../../Hooks';

const Container = styled.form`
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-rows: max-content;
  padding: 30px;
  grid-gap: 30px;
`;

const Header = styled.div`
  display: grid;
  grid-template-columns: max-content 1fr 150px;
  align-items: center;
  grid-column-gap: 15px;
`;

const Content = styled.div`
  display: grid;
  grid-auto-rows: max-content;
  grid-gap: 15px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 250px) 150px;
  grid-gap: 15px;
  align-items: center;
  min-height: 50px;
`;

const Tools = styled.div`
  display: grid;
  grid-template-columns: max-content max-content;
  justify-content: flex-end;
  align-items: center;
  grid-column-gap: 15px;
`;

const CalendarBookingForm = ({ data, close }) => {
  const dispatch = useDispatch();
  const date = new Date(data.cell).toLocaleDateString('sv-SV');
  const startTime = new Date(data.cell).toLocaleTimeString('sv-SV', { hour: '2-digit', minute: '2-digit' });
  const endTime = new Date(addHours(data.cell, 1)).toLocaleTimeString('sv-SV', { hour: '2-digit', minute: '2-digit' });
  const add = useAfterSuccess(useAddBasketProduct(), close);

  const { values, handleChange } = useForm({
    date,
    startTime,
    endTime,
    wholeDay: false,
    repeatUntil: new Date(date),
    week: getWeekNumber(new Date(date)),
    type: ''
  });

  const getRepeated = useCallback(() => {
    if (values.repeatEvery) {
      const [h, m] = values.endTime.split(':').map(x => parseInt(x, 10));
      const until = new Date(values.repeatUntil);
      until.setHours(h, m);
      return {
        repeatUntil: ISODateString(until),
        repeatEvery: values.repeatEvery
      };
    }
    return undefined;
  }, [values.endTime, values.repeatEvery, values.repeatUntil]);

  const handleSubmit = async e => {
    e.preventDefault();

    if (!isValidTime(values.startTime)) {
      dispatch(addErr('Starttid har fel format.'));
      return;
    }

    if (!isValidTime(values.endTime)) {
      dispatch(addErr('Sluttid har fel format.'));
      return;
    }

    if (!values.wholeDay) {
      if (timeBetween(values.date, values.startTime, values.endTime) < 0) {
        dispatch(addErr('Start kan inte vara efter slut.'));
        return;
      }
      if (values.startTime === values.endTime) {
        dispatch(addErr('Start och slut kan inte vara samma.'));
        return;
      }
    }

    if (values.repeatEvery) {
      if (isSameDate(values.date, values.repeatUntil)) {
        dispatch(addErr('Start och sluta repetera kan inte vara samma datum.'));
        return;
      }

      if (new Date(values.date).getTime() > new Date(values.repeatUntil).getTime()) {
        dispatch(addErr('Sluta repetera kan inte vara före startdatum.'));
        return;
      }
    }

    const body = {
      productID: data.data.id,
      type: data.data.type,
      startTime: parseTime(values.date, values.wholeDay ? '00:00' : values.startTime),
      endTime: parseTime(values.date, values.wholeDay ? '23:59' : values.endTime),
      spec: {
        version: values.wholeDay ? 'DAY' : 'HOUR',
        comment: values.comment
      }
    };
    body.spec = {
      ...body.spec,
      ...getRepeated()
    };
    add.request(body);
  };

  return (
    <Container onSubmit={handleSubmit}>
      <Header>
        <Avatar image={data.data.image} initials={data.data.name.charAt(0)} size="60px" />
        <h4>{cap(data.data.name)}</h4>
        <Checkbox name="wholeDay" value={values.wholeDay} onChange={handleChange} label="Heldag" />
      </Header>
      <Content>
        <Row style={{ zIndex: ZIndex.dropdown - 1 }}>
          <Input label="Datum" name="date" value={values.date} onChange={handleChange} type="text" />
          {!values.wholeDay && (
            <>
              <Input
                label="Start"
                name="startTime"
                value={values.startTime}
                onChange={handleChange}
                type="text"
                pattern={Regexp.time}
              />
              <Input
                label="Slut"
                name="endTime"
                value={values.endTime}
                onChange={handleChange}
                type="text"
                pattern={Regexp.time}
              />
              <h4>{`${timeBetween(values.date, values.startTime, values.endTime)} h`}</h4>
            </>
          )}
        </Row>
        <Row style={{ zIndex: ZIndex.dropdown }}>
          <Select
            value={values.repeatEvery}
            onChange={handleChange}
            name="repeatEvery"
            label="Repetera"
            options={CALENDAR_REPEAT}
          />
          {values.repeatEvery && (
            <DatePicker
              label="Sluta repetera den"
              name="repeatUntil"
              value={values.repeatUntil}
              onChange={handleChange}
            />
          )}
        </Row>
      </Content>
      <Tools>
        <Cancel value="Avbryt" onClick={close} />
        <Button type="submit" value="Lägg till i varukorgen" />
      </Tools>
    </Container>
  );
};

CalendarBookingForm.propTypes = {
  data: PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.number, PropTypes.array, PropTypes.object])
    .isRequired,
  close: PropTypes.func.isRequired
};

export default CalendarBookingForm;
